/**
 * NotificationToastProvider
 *
 * Bridges the realtime notification feed to the canonical toast system
 * (`useToast()` from contexts/ToastContext — NOT sonner, per CLAUDE.md).
 *
 * Watches the live `useNotifications` snapshot and fires a toast for each
 * newly-arrived notification (a doc whose id wasn't in the previous snapshot).
 * Only `critical` + `warning` severities toast — `info` stays silent in the
 * bell to avoid toast spam on routine completions. Severity drives duration
 * (critical 10s / warning 6s). When the notification has a derivable
 * destination, the toast gets a "View" action sharing the exact nav logic the
 * bell item + page use (`getNotificationNavUrl`).
 *
 * Renders nothing — it's a side-effect-only bridge mounted inside AppShell.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useNotifications, getSeverityStyle } from '@/hooks/useNotifications';
import { getNotificationNavUrl } from '@/services/notificationNav';

export function NotificationToastProvider() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  // Realtime feed (per-user read state already baked in by the hook).
  const { notifications, loading } = useNotifications({ limit: 20, realtime: true });

  // Ids we've already seen, so we only toast genuinely-new arrivals. Seeded on
  // first load so we don't toast the entire backlog when the app mounts.
  // AppShell keys this provider on uid+orgId, so a user/org switch remounts it
  // and re-baselines against the new identity's snapshot (no cross-org spam).
  const seenIds = useRef<Set<string> | null>(null);

  useEffect(() => {
    // Wait for auth to resolve: while `uid` is undefined, parseNotification
    // falls back to the legacy org-wide `read` flag, which must never decide
    // whether a per-user toast fires.
    if (loading || !uid) return;

    // First settled snapshot: record the baseline, toast nothing.
    if (seenIds.current === null) {
      seenIds.current = new Set(notifications.map((n) => n.id));
      return;
    }

    const previous = seenIds.current;
    const next = new Set<string>();

    for (const n of notifications) {
      next.add(n.id);
      if (previous.has(n.id)) continue; // already seen
      // Brand-new notification → maybe toast.
      if (n.severity !== 'critical' && n.severity !== 'warning') continue;
      // Don't toast something the user has already read (e.g. acted on it on
      // another device before this tab caught up).
      if (n.read) continue;

      const { toastType, toastDuration } = getSeverityStyle(n.severity);
      const url = getNotificationNavUrl(n);

      toast({
        type: toastType,
        title: n.title,
        message: n.message,
        duration: toastDuration,
        ...(url
          ? { action: { label: 'View', onClick: () => navigate(url) } }
          : {}),
      });
    }

    seenIds.current = next;
  }, [notifications, loading, uid, toast, navigate]);

  return null;
}
